import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Lock, Globe2, Users } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const HERO_IMG =
  "https://images.unsplash.com/photo-1768885514740-d64d25ac9a64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxwZW9wbGUlMjBjbHViJTIwc2lsaG91ZXR0ZSUyMGRhcmt8ZW58MHx8fHwxNzc3Njg3MTI5fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const nav = useNavigate();
  const { login, register, guest } = useAuth();
  const [loading, setLoading] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ email: "", username: "", password: "" });
  const [guestName, setGuestName] = useState("");

  const handle = (fn) => async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fn();
      toast.success("Welcome inside Xhat.");
      nav("/lobby");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grain">
      {/* Hero background */}
      <div className="fixed inset-0 -z-10">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-[#050505]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-24">
        {/* Top bar */}
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#e81cff] to-[#ff2a5f] shadow-[0_0_24px_rgba(232,28,255,0.55)] hue-spin" />
            <span className="font-display font-black tracking-tighter text-xl">Xhat</span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-400">
            <Lock className="w-3.5 h-3.5" /> Private · Worldwide
          </div>
        </div>

        {/* Bento hero */}
        <div className="mt-12 grid grid-cols-12 gap-6">
          {/* Left: copy */}
          <div className="col-span-12 lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 text-xs tracking-widest uppercase text-fuchsia-300">
              <Sparkles className="w-3.5 h-3.5" /> Open tonight
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter mt-6 leading-[0.95]">
              a <span className="italic text-fuchsia-400">private</span> room <br />
              for every <span className="bg-gradient-to-r from-[#e81cff] via-[#ff2a5f] to-[#00f0ff] bg-clip-text text-transparent">version</span> of you.
            </h1>
            <p className="text-zinc-300 text-lg mt-6 max-w-xl leading-relaxed">
              Drop into worldwide chat rooms or start a mini room — just you and three others.
              No feeds. No likes. Just real talk, behind the curtain.
            </p>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl">
              <Feature icon={<Globe2 className="w-4 h-4" />} title="Global rooms" sub="Pride · Regions · Vibes" />
              <Feature icon={<Users className="w-4 h-4" />} title="Mini rooms" sub="Max 4 people" />
              <Feature icon={<Lock className="w-4 h-4" />} title="Invite-only" sub="Secretive by default" />
            </div>
          </div>

          {/* Right: auth card */}
          <div className="col-span-12 lg:col-span-5">
            <div className="glass rounded-3xl p-6 sm:p-8 shadow-[0_0_80px_rgba(232,28,255,0.12)]">
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-3 bg-black/40 p-1 rounded-full border border-white/5">
                  <TabsTrigger value="login" data-testid="tab-login" className="rounded-full text-sm data-[state=active]:bg-[#e81cff] data-[state=active]:text-white">
                    Sign in
                  </TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register" className="rounded-full text-sm data-[state=active]:bg-[#e81cff] data-[state=active]:text-white">
                    Create
                  </TabsTrigger>
                  <TabsTrigger value="guest" data-testid="tab-guest" className="rounded-full text-sm data-[state=active]:bg-[#e81cff] data-[state=active]:text-white">
                    Guest
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="login" className="mt-6">
                  <form className="space-y-4" onSubmit={handle(() => login(loginForm.email, loginForm.password))}>
                    <input data-testid="login-email-input" type="email" required placeholder="you@midnight.com" className="text-input" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
                    <input data-testid="login-password-input" type="password" required placeholder="password" className="text-input" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
                    <button data-testid="login-submit-btn" disabled={loading} className="neon-btn w-full">
                      {loading ? "Entering..." : "Enter the room"}
                    </button>
                  </form>
                </TabsContent>

                <TabsContent value="register" className="mt-6">
                  <form className="space-y-4" onSubmit={handle(() => register(regForm.email, regForm.password, regForm.username))}>
                    <input data-testid="register-username-input" required placeholder="pick a handle" className="text-input" value={regForm.username} onChange={(e) => setRegForm({ ...regForm, username: e.target.value })} />
                    <input data-testid="register-email-input" type="email" required placeholder="email" className="text-input" value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} />
                    <input data-testid="register-password-input" type="password" required minLength={6} placeholder="password (6+ chars)" className="text-input" value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} />
                    <button data-testid="register-submit-btn" disabled={loading} className="neon-btn w-full">
                      {loading ? "Creating..." : "Create your Xhat key"}
                    </button>
                  </form>
                </TabsContent>

                <TabsContent value="guest" className="mt-6">
                  <form className="space-y-4" onSubmit={handle(() => guest(guestName))}>
                    <p className="text-sm text-zinc-400">Slip in anonymously. No email, no trace. Just a name.</p>
                    <input data-testid="guest-username-input" required minLength={2} placeholder="what do we call you?" className="text-input" value={guestName} onChange={(e) => setGuestName(e.target.value)} />
                    <button data-testid="guest-submit-btn" disabled={loading} className="neon-btn w-full">
                      {loading ? "Entering..." : "Enter as guest"}
                    </button>
                  </form>
                </TabsContent>
              </Tabs>

              <p className="text-[11px] tracking-widest uppercase text-zinc-500 mt-6 text-center">
                18+ · Be kind · Zero tolerance for hate
              </p>
            </div>
          </div>
        </div>

        <footer className="mt-20 pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-4 text-xs text-zinc-500">
          <div>© 2026 Xhat · A private space for our community.</div>
          <nav className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
            <a href="mailto:hello@xhatroom.com" className="hover:text-white transition-colors">Support</a>
          </nav>
        </footer>
      </div>
    </div>
  );
}

function Feature({ icon, title, sub }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4 card-lift">
      <div className="w-8 h-8 rounded-lg bg-[#121215] border border-white/10 flex items-center justify-center text-fuchsia-400">
        {icon}
      </div>
      <div className="mt-3 font-display font-bold">{title}</div>
      <div className="text-xs text-zinc-500 mt-1">{sub}</div>
    </div>
  );
}
